// FILE: src/components/case-studies/learner/CaseRunner.js
//
// The learner case workspace (Case Study Question Framework, PR 3).
// Desktop: ~58/42 split (case panel | question panel). Narrow screens:
// Case / Question tab toggle — answer drafts live in this component's state,
// so switching tabs never loses them.
//
// Server truths only: reveal state and correctness always come from the
// RPCs. submit_case_answer returns any newly unlocked stage payload, which
// we merge locally — the client never decides what is revealed.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { submitCaseAnswer } from '../../../services/caseStudyService';
import { getQuestionType } from '../../../questionTypes';
import { trackEvent } from '../../../services/telemetryService';
import CasePanel from './CasePanel';
import {
  draftComplete, firstUnanswered, initialDraft, mergeUnlockedStage, progressOf,
} from './caseRunnerUtils';

const T = {
  line: 'var(--line, #e5e8f1)', muted: 'var(--muted, #5a6582)', ink: 'var(--ink, #1a2138)',
  brand: 'var(--brand, #2c63ff)', brandSoft: 'var(--brand-soft, #eaf0ff)',
  good: 'var(--good, #16a571)', goodSoft: 'var(--good-soft, #e6f7ef)',
  bad: 'var(--bad, #d64545)', badSoft: 'var(--bad-soft, #fde9ea)',
  panel: 'var(--panel, #ffffff)', warnSoft: 'var(--warn-soft, #fff4dc)',
};
const card = { background: T.panel, border: `1px solid ${T.line}`, borderRadius: 12, padding: 16 };

export default function CaseRunner({ caseMeta, attempt, initialStages, initialResponses, onCompleted, narrow = false }) {
  const [stages, setStages] = useState(initialStages);
  const [responses, setResponses] = useState(initialResponses || []);
  const [feedback, setFeedback] = useState(null);   // practice-mode result of the last submit
  const [draft, setDraft] = useState([]);
  const [flagged, setFlagged] = useState(false);
  const [changes, setChanges] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('question'); // narrow screens only
  const startRef = useRef(Date.now());
  const firstTouchRef = useRef(null);

  const active = useMemo(() => firstUnanswered(stages, responses), [stages, responses]);
  const progress = progressOf(stages, responses, attempt.question_count);

  useEffect(() => {
    if (active) {
      setDraft(initialDraft(active));
      setFlagged(false); setChanges(0); setFeedback(null);
      startRef.current = Date.now(); firstTouchRef.current = null;
    }
  }, [active && active.case_sequence]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!active && !feedback) return null; // parent moves to completion

  function onDraftChange(next) {
    if (firstTouchRef.current === null) firstTouchRef.current = Date.now();
    if (draft.length > 0) setChanges((c) => c + 1);
    setDraft(next);
    try { trackEvent('case_answer_changed', 'learning', { case_id: caseMeta.id, case_sequence: active.case_sequence }); } catch (_) { /* noop */ }
  }

  async function submit() {
    setBusy(true); setError(null);
    const timeSpent = Math.round((Date.now() - startRef.current) / 1000);
    const firstAnswer = firstTouchRef.current ? Math.round((firstTouchRef.current - startRef.current) / 1000) : null;
    try {
      const def = getQuestionType(active.type);
      const normalized = def ? def.normalizeAnswer(draft) : draft;
      const res = await submitCaseAnswer(attempt.id, active.case_sequence, normalized, {
        flagged, timeSpentSeconds: timeSpent, firstAnswerSeconds: firstAnswer, answerChangeCount: changes,
      });
      try {
        trackEvent('case_question_answered', 'learning', {
          case_id: caseMeta.id, case_sequence: active.case_sequence, question_type: active.type, mode: attempt.mode,
        });
        if (flagged) trackEvent('case_question_flagged', 'learning', { case_id: caseMeta.id, case_sequence: active.case_sequence });
        if (res.unlocked_stage) trackEvent('case_stage_unlocked', 'learning', { case_id: caseMeta.id, stage_sequence: res.unlocked_stage.sequence });
      } catch (_) { /* telemetry never blocks */ }

      const newResponse = { case_sequence: active.case_sequence, selected_answer: normalized,
        is_correct: res.is_correct ?? null, question_type: active.type };
      const nextResponses = [...responses, newResponse];
      const nextStages = mergeUnlockedStage(stages, res.unlocked_stage);

      if (attempt.mode === 'practice') {
        // Show feedback inline; Next advances (and reveals unlocked content).
        setFeedback({ res, question: active, stagedResponses: nextResponses, stagedStages: nextStages });
      } else if (res.case_completed) {
        onCompleted(res.summary, nextStages, nextResponses);
      } else {
        setResponses(nextResponses); setStages(nextStages);
        if (res.unlocked_stage && narrow) setTab('case');
      }
      if (attempt.mode === 'practice' && res.case_completed) {
        // handled on Next click so the learner sees the last rationale first
      }
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  function next() {
    const { res, stagedResponses, stagedStages } = feedback;
    setFeedback(null);
    if (res.case_completed) { onCompleted(res.summary, stagedStages, stagedResponses); return; }
    setResponses(stagedResponses); setStages(stagedStages);
    if (res.unlocked_stage && narrow) setTab('case');
  }

  const def = active ? getQuestionType(active.type) : null;
  const Renderer = def && def.LearnerRenderer;
  const q = feedback ? feedback.question : active;

  const questionPanel = (
    <div style={{ ...card }} data-testid="question-panel">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontWeight: 800, fontSize: '0.85rem' }}>Question {q.case_sequence} of {progress.total}</span>
        <span style={{ fontSize: '0.7rem', color: T.muted, textTransform: 'uppercase' }}>{q.type.replace('_', ' ')}</span>
        <span style={{ flex: 1 }} />
        {!feedback && (
          <label style={{ fontSize: '0.76rem', color: flagged ? T.brand : T.muted, cursor: 'pointer' }}>
            <input type="checkbox" checked={flagged} onChange={(e) => setFlagged(e.target.checked)} data-testid="flag-question" /> 🚩 Flag
          </label>
        )}
      </div>
      <div style={{ fontSize: '0.95rem', lineHeight: 1.5, marginBottom: 12 }}>{q.question}</div>

      {!feedback && Renderer && (
        <Renderer question={q} value={draft} onChange={onDraftChange} disabled={busy} />
      )}
      {!feedback && !Renderer && (
        <div style={{ color: T.bad }}>This question type isn't supported yet.</div>
      )}

      {feedback && attempt.mode === 'practice' && (
        <div data-testid="practice-feedback">
          {(() => {
            const ReviewR = getQuestionType(q.type)?.ReviewRenderer;
            return ReviewR ? (
              <ReviewR question={q} selected={feedback.stagedResponses[feedback.stagedResponses.length - 1].selected_answer}
                correct={feedback.res.correct_answers || []} />
            ) : null;
          })()}
          <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 8,
            background: feedback.res.is_correct ? T.goodSoft : T.badSoft,
            border: `1px solid ${feedback.res.is_correct ? T.good : T.bad}` }}>
            <strong style={{ color: feedback.res.is_correct ? T.good : T.bad }}>
              {feedback.res.is_correct ? '✓ Correct' : '✕ Not quite'}
            </strong>
            <div style={{ fontSize: '0.86rem', marginTop: 6, whiteSpace: 'pre-wrap' }}>{feedback.res.explanation}</div>
          </div>
          {feedback.res.unlocked_stage && (
            <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 8, background: T.brandSoft, color: T.brand, fontSize: '0.82rem', fontWeight: 700 }}
              data-testid="unlock-notice">
              ✦ New case information is available: {feedback.res.unlocked_stage.title}
            </div>
          )}
        </div>
      )}

      {error && <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 8, background: T.badSoft, color: T.bad, fontSize: '0.82rem' }} data-testid="runner-error">{error}</div>}

      <div style={{ display: 'flex', gap: 8, marginTop: 14, alignItems: 'center' }}>
        {!feedback ? (
          <button type="button" data-testid="submit-answer" disabled={busy || !draftComplete(q, draft)}
            style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: T.brand, color: '#fff', fontWeight: 700, cursor: 'pointer', opacity: busy || !draftComplete(q, draft) ? 0.5 : 1 }}
            onClick={submit}>
            {busy ? 'Submitting…' : 'Submit answer'}
          </button>
        ) : (
          <button type="button" data-testid="next-question"
            style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: T.brand, color: '#fff', fontWeight: 700, cursor: 'pointer' }}
            onClick={next}>
            {feedback.res.case_completed ? 'See results →' : 'Next question →'}
          </button>
        )}
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: '0.76rem', color: T.muted }} data-testid="progress-label">
          {progress.answered} of {progress.total} answered
        </span>
      </div>
      <div aria-hidden="true" style={{ height: 6, borderRadius: 3, background: T.line, marginTop: 8 }}>
        <div style={{ width: `${progress.pct}%`, height: 6, borderRadius: 3, background: T.brand, transition: 'width .3s' }} />
      </div>
    </div>
  );

  const casePanel = (
    <div style={{ ...card, maxHeight: narrow ? 'none' : '72vh', overflowY: 'auto' }}>
      <CasePanel caseMeta={caseMeta} stages={stages} latestStage={stages[stages.length - 1]?.sequence}
        onArtifactViewed={(a) => { try { trackEvent('case_artifact_viewed', 'learning', { case_id: caseMeta.id, artifact_id: a.id }); } catch (_) { /* noop */ } }} />
    </div>
  );

  if (narrow) {
    return (
      <div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
          {['case', 'question'].map((t) => (
            <button key={t} type="button" data-testid={`tab-${t}`}
              style={{ flex: 1, padding: '8px 0', borderRadius: 8, fontWeight: 700, cursor: 'pointer',
                border: `1px solid ${tab === t ? T.brand : T.line}`,
                background: tab === t ? T.brand : T.panel, color: tab === t ? '#fff' : T.ink }}
              onClick={() => setTab(t)}>
              {t === 'case' ? '📖 Case' : '❓ Question'}
            </button>
          ))}
        </div>
        {tab === 'case' ? casePanel : questionPanel}
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 58fr) minmax(0, 42fr)', gap: 16 }} data-testid="case-workspace">
      {casePanel}
      {questionPanel}
    </div>
  );
}
